export type MediaRecordingKind = 'audio' | 'screen'

export interface AudioInputDeviceOption {
  deviceId: string
  label: string
}

function normalizeRecorderMimeType(mimeType: string | null | undefined): string {
  return mimeType?.split(';')[0]?.trim().toLowerCase() ?? ''
}

const AUDIO_RECORDING_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
] as const

const SCREEN_RECORDING_MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
] as const

function getTimestamp(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}${month}${day}-${hours}${minutes}${seconds}`
}

function getPreferredMimeCandidates(kind: MediaRecordingKind): readonly string[] {
  return kind === 'audio' ? AUDIO_RECORDING_MIME_CANDIDATES : SCREEN_RECORDING_MIME_CANDIDATES
}

export function isMediaRecordingSupported(kind: MediaRecordingKind): boolean {
  if (typeof navigator === 'undefined' || typeof MediaRecorder === 'undefined') {
    return false
  }

  if (!navigator.mediaDevices) {
    return false
  }

  if (kind === 'audio') {
    return typeof navigator.mediaDevices.getUserMedia === 'function'
  }

  return typeof navigator.mediaDevices.getDisplayMedia === 'function'
}

export function resolvePreferredRecordingMimeType(kind: MediaRecordingKind): string | null {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return null
  }

  for (const candidate of getPreferredMimeCandidates(kind)) {
    if (MediaRecorder.isTypeSupported(candidate)) {
      return candidate
    }
  }

  return null
}

export async function requestRecordingStream(
  kind: MediaRecordingKind,
  options?: { audioDeviceId?: string | null; includeMicrophone?: boolean },
): Promise<MediaStream> {
  if (!navigator.mediaDevices) {
    throw new Error('当前浏览器不支持媒体采集。请使用最新版 Chrome 或 Edge。')
  }

  if (kind === 'audio') {
    if (typeof navigator.mediaDevices.getUserMedia !== 'function') {
      throw new Error('当前浏览器不支持麦克风录音。请使用最新版 Chrome 或 Edge。')
    }

    return navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: options?.audioDeviceId ? { exact: options.audioDeviceId } : undefined,
        echoCancellation: true,
        noiseSuppression: true,
      },
    })
  }

  if (typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
    throw new Error('当前浏览器不支持屏幕录制。请使用最新版 Chrome 或 Edge。')
  }

  const displayStream = await navigator.mediaDevices.getDisplayMedia({
    video: true,
    audio: false,
  })

  if (!options?.includeMicrophone) {
    return displayStream
  }

  if (typeof navigator.mediaDevices.getUserMedia !== 'function') {
    displayStream.getTracks().forEach((track) => track.stop())
    throw new Error('当前浏览器不支持麦克风录音。请使用最新版 Chrome 或 Edge。')
  }

  try {
    const microphoneStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: options.audioDeviceId ? { exact: options.audioDeviceId } : undefined,
        echoCancellation: true,
        noiseSuppression: true,
      },
    })

    return new MediaStream([
      ...displayStream.getVideoTracks(),
      ...microphoneStream.getAudioTracks(),
    ])
  } catch (error) {
    displayStream.getTracks().forEach((track) => track.stop())
    const message = error instanceof Error ? error.message : '无法启用麦克风录制。'
    throw new Error(message)
  }
}

export async function listAudioInputDevices(): Promise<AudioInputDeviceOption[]> {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return []
  }

  const devices = await navigator.mediaDevices.enumerateDevices()
  return devices
    .filter((device) => device.kind === 'audioinput')
    .map((device, index) => ({
      deviceId: device.deviceId,
      label: device.label || `录音设备 ${index + 1}`,
    }))
}

function getFileExtension(mimeType: string, kind: MediaRecordingKind): string {
  if (mimeType.includes('ogg')) {
    return '.ogg'
  }

  if (mimeType.includes('wav')) {
    return '.wav'
  }

  if (mimeType.includes('mp4')) {
    return '.mp4'
  }

  if (mimeType.includes('webm')) {
    return '.webm'
  }

  return kind === 'audio' ? '.webm' : '.webm'
}

export function buildRecordedFile(params: {
  blob: Blob
  kind: MediaRecordingKind
  mimeType?: string | null
  now?: Date
}): File {
  const resolvedMimeType =
    normalizeRecorderMimeType(params.mimeType) ||
    normalizeRecorderMimeType(params.blob.type) ||
    (params.kind === 'audio' ? 'audio/webm' : 'video/webm')
  const prefix = params.kind === 'audio' ? 'voice-recording' : 'screen-recording'
  const extension = getFileExtension(resolvedMimeType, params.kind)
  const fileName = `${prefix}-${getTimestamp(params.now ?? new Date())}${extension}`

  return new File([params.blob], fileName, {
    type: resolvedMimeType,
    lastModified: (params.now ?? new Date()).getTime(),
  })
}

export function formatRecordingDuration(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(safeSeconds / 60)
  const seconds = safeSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}